import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import BrandIcon from "../BrandIcon";
import { tokens } from "../../theme";
import type { WidgetFeatures } from "../../types/api";
import { DEFAULT_FEATURES } from "../../lib/configHelpers";

interface MobileComposerProps {
  name: string;
  onSend: (text: string) => void;
  disabled?: boolean;
  showPoweredBy?: boolean;
  features?: WidgetFeatures;
}

const styles = {
  footer: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    padding: `${tokens.space[2]} ${tokens.space[4]} 0`,
    paddingBottom: `max(var(--avatar-ime-bottom, 0px), var(--avatar-kb-safe-bottom))`,
    background: tokens.surface.canvas,
  },
  fieldShell: {
    padding: 4,
    border: `1.5px solid ${tokens.surface.elevated}`,
    borderRadius: tokens.radius.pill,
    background: tokens.surface.elevated,
    boxSizing: 'border-box',
  },
  fieldWrap: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[2],
    minHeight: 48,
    height: 48,
    boxSizing: "border-box",
    padding: `0 6px 0 ${tokens.space[4]}`,
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.pill,
    background: tokens.surface.input,
  },
  field: {
    flex: 1,
    minWidth: 0,
    height: 44,
    margin: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    outline: "none",
    boxShadow: "none",
    appearance: "none",
    WebkitAppearance: "none",
    fontSize: "16px",
    lineHeight: "44px",
    fontFamily: "inherit",
    color: tokens.text.primary,
  },
  micBtn: {
    width: 36,
    height: 36,
    border: "none",
    background: "transparent",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: tokens.gradient.accent,
    color: tokens.text.onBrand,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: tokens.shadow.send,
    padding: 0,
    flexShrink: 0,
  },
  powered: {
    margin: `${tokens.space[2]} 0 0`,
    paddingBottom: tokens.space[2],
    textAlign: "center",
    fontSize: 14,
    color: tokens.text.muted,
    fontWeight: tokens.font.weight.medium,
  },
} as const satisfies Record<string, CSSProperties>;

export default function MobileComposer({
  name,
  onSend,
  disabled,
  showPoweredBy = true,
  features = DEFAULT_FEATURES,
}: MobileComposerProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const canSend = value.trim().length > 0 && !disabled;
  const idle = !canSend;

  useEffect(() => {
    if (disabled) inputRef.current?.blur();
  }, [disabled]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submit();
    }
  };

  return (
    <footer style={styles.footer}>
      <form onSubmit={handleSubmit}>
        <div style={styles.fieldShell}>
          <div style={styles.fieldWrap}>
          <input
            ref={inputRef}
            style={{
              ...styles.field,
              pointerEvents: disabled ? 'none' : 'auto',
            }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={(e) => {
              if (disabled) e.currentTarget.blur();
            }}
            placeholder={t("mobile.placeholder", { name })}
            readOnly={disabled}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            enterKeyHint="send"
            autoComplete="off"
            autoCorrect="off"
            aria-label={t("input.ariaLabel")}
          />
          {features.voice ? (
            <button
              type="button"
              style={styles.micBtn}
              disabled={disabled}
              aria-label={t("mobile.mic")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="9"
                  y="3"
                  width="6"
                  height="11"
                  rx="3"
                  stroke={tokens.text.secondary}
                  strokeWidth="1.8"
                />
                <path
                  d="M6 11a6 6 0 0012 0"
                  stroke={tokens.text.secondary}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M12 17v3"
                  stroke={tokens.text.secondary}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
          <button
            type="submit"
            style={{
              ...styles.sendBtn,
              opacity: idle ? 0.45 : 1,
              boxShadow: idle ? "none" : tokens.shadow.send,
              cursor: canSend ? "pointer" : "default",
            }}
            disabled={!canSend}
            aria-label={t("input.send")}
          >
            <BrandIcon
              name="sendWhite"
              size={16}
              style={{ position: "relative", top: -1, left: 1 }}
            />
          </button>
          </div>
        </div>
      </form>
      {showPoweredBy ? (
        <p style={styles.powered} data-powered-by="">
          {t("input.poweredBy")}
        </p>
      ) : null}
    </footer>
  );
}
